import { LineImageAlt } from '@qr-front/icons/line-image-alt'
import { TFileUploadHandle } from '@qr-front/ui/components/FileUpload'
import { fieldLabelCls } from '@qr-front/ui/components/InputField'
import { cn } from '@qr-front/ui/utils'
import { FileUploadToMediaStorage } from 'components/FileUploadToMediaStorage'
import { isSubmittingAtom } from 'features/generator/GeneratorLayout/GeneratorFooter/NextButton'
import {
	markContentTouched,
	qrTypeAtom,
} from 'features/generator/generatorState'
import { QR_TYPE } from 'features/generator/generatorTypes'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { atomWithReset, RESET } from 'jotai/utils'
import { useTranslations } from 'next-intl'
import {
	forwardRef,
	RefObject,
	useEffect,
	useImperativeHandle,
	useRef,
} from 'react'

import { showNotification } from '@/components/Notification'
import { TUploadedMedia } from '@/services'
import { getStore } from '@/store'

import { GENERATOR_FORM_ANIMATION_DURATION } from '../../components/GeneratorFormSection'
import { TContentFormSectionPartial } from '../qrContentFormTypes'

// 5MB
const MAX_FILE_SIZE = 5 * 1024 * 1024
const MAX_FILE_COUNT = 10

export const imageListAtom = atomWithReset<TUploadedMedia[]>([])
export const imageFileListAtom = atomWithReset<File[]>([])
export const imageLocalPreviewUrlsAtom = atomWithReset<(string | null)[]>([])

export const resetImagesField = () => {
	getStore().set(imageListAtom, RESET)
	getStore().set(imageFileListAtom, RESET)
	getStore().set(imageLocalPreviewUrlsAtom, RESET)
}

const validateImages = ({
	fileUploadRef,
	requiredErrorMessage,
	containerRef,
}: {
	fileUploadRef: RefObject<TFileUploadHandle | null>
	requiredErrorMessage: string
	containerRef: RefObject<HTMLDivElement | null>
}) => {
	const isValid = getStore().get(imageListAtom).length >= 1
	if (!isValid) {
		fileUploadRef.current?.setError?.(requiredErrorMessage)
		setTimeout(() => {
			containerRef.current?.scrollIntoView?.({
				behavior: 'smooth',
				block: 'center',
			})
		}, GENERATOR_FORM_ANIMATION_DURATION * 1.25)
	}
	return isValid
}

export const ImagesField: TContentFormSectionPartial = forwardRef(
	({ onValidChange }, ref) => {
		const t = useTranslations()
		const qrType = useAtomValue(qrTypeAtom)
		const isSocialMediaQrType = qrType === QR_TYPE.SocialMedia

		const uploadErrorMessage = t(
			'generator__images_content_form__images__upload__max_size_error',
			{
				size: MAX_FILE_SIZE / 1024 / 1024,
			},
		)

		const requiredErrorMessage = t(
			'ui__field_validation_errors__generic__required',
		)

		const maxFilesLimitError = t(
			'generator__images_content_form__images__upload__max_files_error',
			{
				count: MAX_FILE_COUNT,
			},
		)

		const [imageList, setImageList] = useAtom(imageListAtom)
		const [fileList, setFileList] = useAtom(imageFileListAtom)
		const setLocalPreviewUrls = useSetAtom(imageLocalPreviewUrlsAtom)
		const blobUrlsRef = useRef<(string | null)[]>([])
		const fileUploadRef = useRef<TFileUploadHandle>(null)
		const containerRef = useRef<HTMLDivElement>(null)
		const isSubmitting = useAtomValue(isSubmittingAtom)

		useEffect(() => {
			return () => {
				blobUrlsRef.current.forEach(
					(url) => url && URL.revokeObjectURL(url),
				)
				getStore().set(imageLocalPreviewUrlsAtom, RESET)
			}
		}, [])

		useEffect(() => {
			const isValid = imageList.length >= 1 || isSocialMediaQrType
			onValidChange(isValid)
		}, [imageList.length, isSocialMediaQrType, onValidChange])

		useImperativeHandle(
			ref,
			() => ({
				validate: () => {
					const areAllImagesUploaded =
						getStore().get(imageListAtom).length ===
						getStore().get(imageFileListAtom).length

					if (areAllImagesUploaded) {
						return new Promise((resolve) => {
							const isValid =
								isSocialMediaQrType ||
								validateImages({
									fileUploadRef,
									requiredErrorMessage,
									containerRef,
								})
							if (isValid) {
								fileUploadRef.current?.clearError?.()
							}
							resolve({ isValid })
						})
					} else {
						return new Promise((resolve) => {
							const check = () => {
								const areAllImagesUploaded =
									getStore().get(imageListAtom).length ===
									getStore().get(imageFileListAtom).length
								if (areAllImagesUploaded) {
									unsubscribeImage()
									unsubscribeFile()
									const isValid = validateImages({
										fileUploadRef,
										requiredErrorMessage,
										containerRef,
									})
									if (isValid) {
										fileUploadRef.current?.clearError?.()
									}
									resolve({ isValid })
								}
							}

							const unsubscribeImage = getStore().sub(
								imageListAtom,
								check,
							)
							const unsubscribeFile = getStore().sub(
								imageFileListAtom,
								check,
							)
						})
					}
				},
			}),
			[isSocialMediaQrType, requiredErrorMessage],
		)

		const handleUploadStart = (file: File, index: number) => {
			markContentTouched()

			const oldUrl = blobUrlsRef.current[index]
			if (oldUrl) URL.revokeObjectURL(oldUrl)
			const url = URL.createObjectURL(file)
			const next = [...blobUrlsRef.current]
			next[index] = url
			blobUrlsRef.current = next
			setLocalPreviewUrls([...next])

			setFileList((prev) => {
				const nextFiles = [...prev]
				nextFiles[index] = file
				return nextFiles
			})
		}

		const handleUploadComplete = (
			uploaded: TUploadedMedia,
			original: File,
			_index: number,
		) => {
			const currentFiles = getStore().get(imageFileListAtom)
			const currentIndex = currentFiles.indexOf(original)

			if (currentIndex === -1) return

			const url = blobUrlsRef.current[currentIndex]
			if (url) URL.revokeObjectURL(url)
			const nextUrls = [...blobUrlsRef.current]
			nextUrls[currentIndex] = null
			blobUrlsRef.current = nextUrls
			setLocalPreviewUrls([...nextUrls])

			setImageList((prev) => {
				const next = [...prev]
				next[currentIndex] = uploaded
				return next
			})
		}

		const handleUploadError = (_file: File, index: number) => {
			const url = blobUrlsRef.current[index]
			if (url) URL.revokeObjectURL(url)
			const nextUrls = blobUrlsRef.current.filter((_, i) => i !== index)
			blobUrlsRef.current = nextUrls
			setLocalPreviewUrls([...nextUrls])
			setFileList((prev) => prev.filter((_, i) => i !== index))
		}

		const handleDelete = (_file: File, indexToDelete: number) => {
			const url = blobUrlsRef.current[indexToDelete]
			if (url) URL.revokeObjectURL(url)
			const nextUrls = blobUrlsRef.current.filter(
				(_, i) => i !== indexToDelete,
			)
			blobUrlsRef.current = nextUrls
			setLocalPreviewUrls([...nextUrls])

			setFileList((prev) =>
				prev.filter((_, index) => index !== indexToDelete),
			)
			setImageList((prev) =>
				prev.filter((_, index) => index !== indexToDelete),
			)
		}

		const onMaxFilesExceeded = () =>
			showNotification(
				t(
					'generator__images_content_form__images__file_limit_exceeded',
					{
						maxFiles: MAX_FILE_COUNT,
					},
				),
				{ type: 'error' },
			)

		const fileUploadTranslations = {
			title: t('generator__images_content_form__images__upload_images'),
			caption: t('generator__images_content_form__images__max_size', {
				size: MAX_FILE_SIZE / 1024 / 1024,
			}),
			unsupportedFormatErrorMessage: t(
				'ui__file_upload__unsupported_format_error',
			),
			sizeTooLargeErrorMessage: uploadErrorMessage,
			maxFilesLimitError: maxFilesLimitError,
		}

		return (
			<div ref={containerRef} className="mt-6 tablet:mt-8">
				<div className="mb-6">
					<label
						className={cn(
							fieldLabelCls,
							'type-heading-4 font-medium',
						)}
					>
						{isSocialMediaQrType
							? t('generator__images_content_form__images__label')
							: t(
									'generator__images_content_form__images__label_required',
								)}
					</label>
					<small className="type-body-small block text-dark-grey">
						{t(
							'generator__images_content_form__images__description',
							{
								count: MAX_FILE_COUNT,
							},
						)}
					</small>
				</div>
				<FileUploadToMediaStorage
					uploadPurpose="content_image"
					ref={fileUploadRef}
					initialFileList={fileList}
					onUploadStart={handleUploadStart}
					onUploadComplete={handleUploadComplete}
					onUploadError={handleUploadError}
					onDelete={handleDelete}
					maxFiles={MAX_FILE_COUNT}
					onMaxFilesExceeded={onMaxFilesExceeded}
					translations={fileUploadTranslations}
					maxFileSize={MAX_FILE_SIZE}
					placeholderIcon={<LineImageAlt />}
					className="mt-2"
					disabled={isSubmitting}
				/>
			</div>
		)
	},
)

ImagesField.displayName = 'ImagesField'
